import { useSearchSession } from './SearchSessionContext';
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useUserStore } from '../../stores/userStore';
import { discoveryApi } from '../../api/discovery';
import AppUiScope from '../../app-ui/AppUiScope';
import baseline, { appCopy, appTextStyle } from '../../app-ui/baseline';
import SearchResultRow from '../../components/content/SearchResultRow';
import { useSearchEditing } from './SearchEditingContext';
import { useSearchAssist } from './useSearchAssist';
import { useRecentSearches, recentSearches } from './recentSearches';
import { rememberSearchSelection } from './continuationArtist';
import { assistItems, emptyAssistItems } from './assistPresentation';
import './SearchAssist.css';

function RecentIcon({ remove = false }) {
  return (
    <svg
      width={remove ? 17 : 18}
      height={remove ? 17 : 18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={remove ? 2 : 1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {remove ? (
        <path d="m18 6-12 12M6 6l12 12" />
      ) : (
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </>
      )}
    </svg>
  );
}
export default function SearchAssist() {
  const session = useSearchSession();
  const editing = useSearchEditing();
  const { locale, t } = useWebLocale();
  const userId = useUserStore((s) => s.user?.id);
  const navigate = useNavigate();
  const client = useQueryClient();
  const recent = useRecentSearches().slice(0, 4);
  const result = useSearchAssist(editing.text, editing.composing);
  const term = editing.text.trim();
  const home = useQuery({
    queryKey: ['web-parity', 'explore'],
    queryFn: ({ signal }) => discoveryApi.home(signal),
    enabled: !term,
    staleTime: 300000,
    retry: false,
  });
  const discovery = client.getQueryData(['web-discovery', userId || 'guest', 'search', locale]);
  const items = term
    ? assistItems(result.data, locale)
    : emptyAssistItems(home.data, discovery, locale);
  const loading = term ? result.waiting : home.isPending;
  const failed = term ? result.isError : home.isError;
  const retry = term ? result.refetch : home.refetch;
  const native = baseline.search.assist;
  const sectionStyle = appTextStyle(baseline.tokens.typography.sectionTitle);
  const submitRecent = (value) => {
    session?.submit(value, 'recent_search');
    recentSearches.add(value);
    editing.close();
    navigate(`/search/summary?q=${encodeURIComponent(value)}`);
  };
  return (
    <AppUiScope className="pp-parity pp-search-assist" surface="lists">
      <div
        className="pp-search-assist-sections"
        style={{ gap: native.searchAssist.gap, paddingTop: native.searchAssist.paddingTop }}
      >
        <section aria-label={appCopy(locale, 'search.recent')}>
          <div
            className="pp-search-recent-heading"
            style={{ marginBottom: native.recentHeading.marginBottom }}
          >
            <h2 style={sectionStyle}>{appCopy(locale, 'search.recent')}</h2>
            {recent.length > 0 && (
              <button
                style={appTextStyle(baseline.contentText.label)}
                onClick={() => recentSearches.clear()}
              >
                {appCopy(locale, 'common.clearAll')}
              </button>
            )}
          </div>
          {recent.length ? (
            <ul className="pp-search-recent-list" style={{ gap: native.recentSection.gap }}>
              {recent.map((value) => (
                <li
                  key={value}
                  style={{ minHeight: native.recentRow.minHeight, gap: native.recentRow.gap }}
                >
                  <RecentIcon />
                  <button
                    className="pp-search-recent-query"
                    style={appTextStyle(baseline.tokens.typography.body)}
                    onClick={() => submitRecent(value)}
                  >
                    {value}
                  </button>
                  <button
                    className="pp-search-recent-remove"
                    aria-label={appCopy(locale, 'search.removeRecent', { query: value })}
                    onClick={() => recentSearches.remove(value)}
                  >
                    <RecentIcon remove />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p
              className="pp-secondary"
              style={{
                ...appTextStyle(baseline.tokens.typography.body),
                marginTop: native.emptyRecentCompact.gap,
              }}
            >
              {appCopy(locale, 'search.noRecent')}
            </p>
          )}
        </section>
        <section
          aria-label={appCopy(locale, 'search.suggestions')}
          className="pp-search-suggestions"
          style={{ gap: native.discoverySection.gap }}
        >
          <h2 style={sectionStyle}>{appCopy(locale, 'search.suggestions')}</h2>
          {loading ? (
            <p role="status">{t('loading')}…</p>
          ) : failed ? (
            <div role="alert">
              <p>{appCopy(locale, 'search.recommendedError')}</p>
              <button className="pp-action" onClick={() => retry()}>
                {appCopy(locale, 'common.retry')}
              </button>
            </div>
          ) : (
            <div className="pp-search-suggestion-rows">
              {items.map(({ kind, item, meta, to, review }, index) => (
                <SearchResultRow
                  key={`${kind}-${index}`}
                  item={item}
                  kind={kind}
                  summary
                  assistMeta={meta}
                  toOverride={to}
                  reviewTone={review}
                  onOpen={() => {
                    if (term) {
                      rememberSearchSelection(kind, item, term, userId);
                      session?.open({ query: term, kind, item, position: 1 });
                    }
                  }}
                />
              ))}
              {!items.length && (
                <p
                  className="pp-secondary"
                  style={{ paddingBlock: native.assistEmpty.paddingVertical }}
                >
                  {appCopy(locale, 'search.noSuggestions')}
                </p>
              )}
            </div>
          )}
        </section>
      </div>
    </AppUiScope>
  );
}
